"use client";

import {
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  blocksEdit,
  countWords,
  GOAL_KINDS,
  type GoalKind,
  goalProgress,
  goalReached,
  IDLE_SECONDS,
  MINUTE_GOALS,
  parseSavedDraft,
  WORD_GOALS,
  writingPace,
} from "@/lib/tool-kit/study-more/freewriting";
import { formatClock } from "@/lib/tool-kit/study-more/noise";

const STORAGE_KEY = "citationeasy-freewriting-v1";
type Status = "ready" | "writing" | "done";
type Visibility = "show" | "fade" | "hide";

/** English strings. A translated content file overrides these through `ui`. */
export const FREEWRITING_TOOL_STRINGS = {
  goalKindLabel: "Write for",
  goalKinds: { minutes: "A set time", words: "A word count", none: "No limit" },
  minutesLabel: "Minutes",
  wordsLabel: "Words",
  visibilityLabel: "Your words on screen",
  visibilities: {
    show: "Show everything",
    fade: "Fade earlier lines",
    hide: "Hide my words while I write",
  },
  visibilityNotes: {
    show: "A normal text box.",
    fade: "Lines near the top of the box fade out, so you stay with the sentence you are writing, not the ones behind you.",
    hide: "The text is invisible as you type (the cursor still moves). It all appears when the session ends.",
  },
  noDelete:
    "Keep me going: turn off Backspace, Delete, undo, paste and editing earlier text",
  noDeleteNote:
    "This is a nudge you choose, not a lock: turn it off or reload the page any time, and your draft is kept. It lifts when the session ends so you can edit.",
  nudgeLabel: "If I stop for",
  nudgeSeconds: "one:{count} second|other:{count} seconds",
  nudgeMessage:
    "Keep going. Write anything, even “I can’t think what to say”, until the next thought comes.",
  writeLabel: "Your freewriting",
  writePlaceholder: "Start typing. The clock starts with your first keystroke.",
  timeLeft: "Time left",
  timeElapsed: "Time elapsed",
  wordCount: "one:{count} word|other:{count} words",
  progressLabel: "Progress towards your goal",
  statusReady: "Ready: the clock starts when you type.",
  statusWriting: "Writing…",
  statusDone: "Time. Well done: your words are yours to edit now.",
  statWords: "Words this session",
  statTime: "Time",
  statPace: "Words per minute",
  restored:
    "Your last draft was restored. New writing is added to the end of it.",
  copyText: "Copy text",
  copied: "Copied!",
  downloadTxt: "Download .txt",
  txtFileName: "freewriting.txt",
  clearDraft: "Clear and start again",
  confirmClear: "Click again to clear your draft",
  storageNote:
    "Your draft is saved in this browser's local storage as you write and is never uploaded. It stays on this device, and clearing site data erases it.",
  storageUnavailable:
    "This browser is not letting the page save data, so your draft will be gone when you close the page. Copy or download it before you leave.",
} as const;

const TEXTAREA_BASE =
  "h-80 w-full resize-none rounded-lg border border-border bg-card p-4 text-base text-foreground leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";
const FADE_MASK =
  "[mask-image:linear-gradient(to_bottom,transparent_0%,black_45%)]";

export default function FreewritingToolWidget() {
  const strings = useToolUi(FREEWRITING_TOOL_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const area = useRef<HTMLTextAreaElement>(null);
  const startedAt = useRef<number | null>(null);
  const lastInput = useRef<number>(0);
  const baseWords = useRef(0);
  const [text, setText] = useState("");
  const [status, setStatus] = useState<Status>("ready");
  const [kind, setKind] = useState<GoalKind>("minutes");
  const [minutes, setMinutes] = useState<string>("10");
  const [words, setWords] = useState<string>("500");
  const [visibility, setVisibility] = useState<Visibility>("show");
  const [lock, setLock] = useState(true);
  const [idleAfter, setIdleAfter] = useState<string>("8");
  const [elapsed, setElapsed] = useState(0);
  const [idle, setIdle] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [restored, setRestored] = useState(false);
  const [clearArmed, setClearArmed] = useState(false);

  const goalAmount = Number(kind === "minutes" ? minutes : words);
  const goal = useMemo(
    () => ({ kind, amount: goalAmount }),
    [kind, goalAmount]
  );
  const sessionWords = Math.max(0, countWords(text) - baseWords.current);
  const locked = lock && status !== "done";

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      setCanSave(false);
    }
    const saved = parseSavedDraft(raw);
    if (saved) {
      setText(saved);
      setRestored(true);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!(loaded && canSave)) {
      return;
    }
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(text));
      } catch {
        setCanSave(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [loaded, canSave, text]);

  useEffect(() => {
    if (status !== "writing") {
      return;
    }
    const id = setInterval(() => {
      const now = Date.now();
      setElapsed(Math.floor((now - (startedAt.current ?? now)) / 1000));
      setIdle(now - lastInput.current > Number(idleAfter) * 1000);
    }, 250);
    return () => clearInterval(id);
  }, [status, idleAfter]);

  useEffect(() => {
    if (status === "writing" && goalReached(goal, sessionWords, elapsed)) {
      setStatus("done");
      setIdle(false);
    }
  }, [status, goal, sessionWords, elapsed]);

  // Native beforeinput: React's onBeforeInput is not the browser event and cannot cancel deletion.
  useEffect(() => {
    const element = area.current;
    if (!(element && locked)) {
      return;
    }
    const guard = (event: InputEvent) => {
      const target = event.target as HTMLTextAreaElement;
      if (
        blocksEdit(
          event.inputType,
          target.selectionStart !== target.selectionEnd
        )
      ) {
        event.preventDefault();
      }
    };
    element.addEventListener("beforeinput", guard);
    return () => element.removeEventListener("beforeinput", guard);
  }, [locked]);

  function change(value: string) {
    if (status === "ready" && value.length > text.length) {
      startedAt.current = Date.now();
      baseWords.current = countWords(text);
      setStatus("writing");
      setElapsed(0);
    }
    lastInput.current = Date.now();
    setIdle(false);
    setText(value);
    setRestored(false);
  }

  function guardKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!locked) {
      return;
    }
    const undo =
      (event.metaKey || event.ctrlKey) &&
      ["z", "y"].includes(event.key.toLowerCase());
    if (event.key === "Backspace" || event.key === "Delete" || undo) {
      event.preventDefault();
    }
  }

  function keepCaretAtEnd() {
    const element = area.current;
    if (element && locked) {
      const end = element.value.length;
      if (element.selectionStart !== end || element.selectionEnd !== end) {
        element.setSelectionRange(end, end);
      }
    }
  }

  function clearDraft() {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setText("");
    setStatus("ready");
    setElapsed(0);
    setIdle(false);
    setRestored(false);
    setClearArmed(false);
    baseWords.current = 0;
    startedAt.current = null;
  }

  const remaining =
    kind === "minutes" ? Math.max(0, goal.amount * 60 - elapsed) : elapsed;
  const shown: Visibility = status === "done" ? "show" : visibility;
  const fillClass = { show: "", fade: FADE_MASK, hide: "text-transparent" }[
    shown
  ];
  const statusText = {
    ready: strings.statusReady,
    writing: strings.statusWriting,
    done: strings.statusDone,
  }[status];

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-5`}>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="fw-kind"
            label={strings.goalKindLabel}
            onChange={setKind}
            options={GOAL_KINDS.map((value) => ({
              value,
              label: strings.goalKinds[value],
            }))}
            value={kind}
          />
          {kind === "minutes" ? (
            <SelectField
              id="fw-minutes"
              label={strings.minutesLabel}
              onChange={setMinutes}
              options={MINUTE_GOALS.map((value) => ({
                value: String(value),
                label: String(value),
              }))}
              value={minutes}
            />
          ) : null}
          {kind === "words" ? (
            <SelectField
              id="fw-words"
              label={strings.wordsLabel}
              onChange={setWords}
              options={WORD_GOALS.map((value) => ({
                value: String(value),
                label: fmt(value),
              }))}
              value={words}
            />
          ) : null}
          <SelectField
            id="fw-idle"
            label={strings.nudgeLabel}
            onChange={setIdleAfter}
            options={IDLE_SECONDS.map((value) => ({
              value: String(value),
              label: tPlural(strings.nudgeSeconds, locale, value),
            }))}
            value={idleAfter}
          />
        </div>
        <PillGroup
          label={strings.visibilityLabel}
          name="fw-visibility"
          onChange={setVisibility}
          options={(["show", "fade", "hide"] as const).map((value) => ({
            value,
            label: strings.visibilities[value],
          }))}
          value={visibility}
        />
        <p className={HINT}>{strings.visibilityNotes[visibility]}</p>
        <Checkbox checked={lock} label={strings.noDelete} onChange={setLock} />
        <p className={HINT}>{strings.noDeleteNote}</p>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className={WIDGET_H2}>
            <label htmlFor="fw-write">{strings.writeLabel}</label>
          </h2>
          <p className="text-foreground text-sm tabular-nums">
            {kind === "words" ? null : (
              <>
                {kind === "none" ? strings.timeElapsed : strings.timeLeft}:{" "}
                <strong>{formatClock(remaining)}</strong>
                {" · "}
              </>
            )}
            {tPlural(strings.wordCount, locale, sessionWords, {
              count: fmt(sessionWords),
            })}
          </p>
        </div>
        {kind === "none" ? null : (
          <progress
            aria-label={strings.progressLabel}
            className="h-2 w-full overflow-hidden rounded-full accent-primary"
            max={1}
            value={goalProgress(goal, sessionWords, elapsed)}
          />
        )}
        <textarea
          aria-describedby="fw-status"
          className={`${TEXTAREA_BASE} ${fillClass} ${idle ? "border-primary ring-2 ring-primary/40" : ""} caret-foreground`}
          id="fw-write"
          onChange={(event) => change(event.target.value)}
          onClick={keepCaretAtEnd}
          onKeyDown={guardKey}
          onKeyUp={keepCaretAtEnd}
          onSelect={keepCaretAtEnd}
          placeholder={strings.writePlaceholder}
          ref={area}
          spellCheck={false}
          value={text}
        />
        <p aria-live="polite" className={HINT} id="fw-status">
          {idle && status === "writing" ? strings.nudgeMessage : statusText}
        </p>
        {restored ? <Notice>{strings.restored}</Notice> : null}
      </section>

      {status === "done" ? (
        <section aria-live="polite" className={`${CARD} space-y-4`}>
          <StatGrid>
            <StatCard label={strings.statWords} value={fmt(sessionWords)} />
            <StatCard label={strings.statTime} value={formatClock(elapsed)} />
            <StatCard
              emphasis
              label={strings.statPace}
              value={fmt(writingPace(sessionWords, elapsed))}
            />
          </StatGrid>
        </section>
      ) : null}

      <section className="space-y-3">
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyText}
            text={text}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={text === ""}
            onClick={() => downloadText(strings.txtFileName, text)}
            type="button"
          >
            {strings.downloadTxt}
          </button>
          <button
            className={BUTTON_SMALL}
            disabled={text === ""}
            onClick={clearDraft}
            type="button"
          >
            {clearArmed ? strings.confirmClear : strings.clearDraft}
          </button>
        </div>
        <p className={HINT}>
          {canSave ? strings.storageNote : strings.storageUnavailable}
        </p>
      </section>
    </div>
  );
}
